/**
 * Formats a date string as relative time (e.g., "5m ago", "2h ago")
 * @param dateString ISO date string to format
 * @returns Formatted relative time string
 */ export function formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
}
/**
 * Describes how fresh a project's last activity is, for at-a-glance scanning.
 * Returns a short label ("Active today", "3d ago", "2mo ago") plus a `stale`
 * flag (true once activity is a week or more old, or missing) so callers can
 * tint idle items. Shared by the dashboard rows and the projects grid cards.
 */ export function staleness(lastActivityAt) {
    if (!lastActivityAt) return {
        label: 'No activity',
        stale: true
    };
    const date = new Date(lastActivityAt);
    if (Number.isNaN(date.getTime())) return {
        label: 'No activity',
        stale: true
    };
    const days = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (days < 1) return {
        label: 'Active today',
        stale: false
    };
    if (days === 1) return {
        label: 'Yesterday',
        stale: false
    };
    if (days < 7) return {
        label: `${days}d ago`,
        stale: false
    };
    if (days < 30) return {
        label: `${Math.floor(days / 7)}w ago`,
        stale: true
    };
    return {
        label: `${Math.floor(days / 30)}mo ago`,
        stale: true
    };
}
/**
 * Time-of-day greeting, optionally personalized with the user's first name.
 * Shared by the dashboard and proposal/program home headers.
 */ export function greeting(name) {
    const hour = new Date().getHours();
    const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    return name ? `${part}, ${name.split(' ')[0]}` : part;
}
/**
 * Descending comparator for ISO date strings, safe against missing/invalid
 * values: those coalesce to epoch 0 (sorted last) so the comparator never
 * returns NaN, which would give implementation-defined ordering.
 */ export function compareByDateDesc(a, b) {
    const toMs = (v)=>{
        if (!v) return 0;
        const ms = new Date(v).getTime();
        return Number.isNaN(ms) ? 0 : ms;
    };
    return toMs(b) - toMs(a);
}
/**
 * Time remaining until a deadline, split into whole days + hours, with an
 * `overdue` flag once the deadline has passed. Clamps at zero so a passed
 * deadline reports 0d 0h rather than negative values.
 */ export function deadlineCountdown(dueDate) {
    const ms = dueDate.getTime() - Date.now();
    const totalHours = Math.max(0, Math.floor(ms / 3600000));
    return {
        days: Math.floor(totalHours / 24),
        hours: totalHours % 24,
        overdue: ms < 0
    };
}
